import { Type } from 'class-transformer';
import {
  IsBoolean,
  IsDate,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Min,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import { ModelViewerLocation } from '../model-viewer-location.dto';
import { DermloopClinicalActionVariantEnum } from '@dermloop/static-values';

export enum ConditionIndexAccurateDiagnosisType {
  Clinical = 'clinical',
  Tele = 'tele',
  Pathology = 'pathology',
}

export enum ConditionIndexActionType {
  ReferralPhysical = 'referralPhysical',
  ReferralTele = 'referralTele',
  ReferralTeleUrgent = 'referralTeleUrgent',
  FollowUp = 'followUp',
  Dismissal = 'dismissal',
  Biopsy = 'biopsy',
  PlannedBiopsy = 'plannedBiopsy',
  NewImageRegistration = 'newImageRegistration',
  TopicalTreatment = 'topicalTreatment',
  NoAction = 'noAction',
}

/**
 * One condition triage as returned by `POST /skin-conditions/search`. The field set is the
 * contract the former search index exposed, kept intact for the desktop and mobile clients.
 */
export class LesionIndexDataDto {
  /**
   * General condition information, always being the latest information available.
   *
   * @deprecated Successor field {@link conditionId}; both are populated.
   */
  @IsUUID()
  lesionId!: string;

  /** Condition id. Successor to the deprecated {@link lesionId}; both are populated. */
  @IsOptional()
  @IsUUID()
  conditionId?: string;

  @IsUUID()
  conditionTypeId!: string;

  @IsUUID()
  /** @deprecated Use conditionTypeId */
  skinConditionTypeId!: string;

  @IsOptional()
  @IsUUID()
  skinTypeId?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  ssn?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  mrn?: string;

  @IsOptional()
  @IsString()
  firstName?: string;

  @IsOptional()
  @IsString()
  lastName?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  fullName?: string;

  @IsOptional()
  @IsDate()
  birthDate?: Date;

  @ValidateIf((o) => o.accurateDiagnosisId)
  @IsEnum(ConditionIndexAccurateDiagnosisType)
  accurateDiagnosisType?: ConditionIndexAccurateDiagnosisType;

  @Type(() => Date)
  @ValidateIf((o) => o.accurateDiagnosisId)
  @IsDate()
  accurateDiagnosisDate?: Date;

  @ValidateIf((o) => o.accurateSubDiagnosisId)
  @IsUUID()
  accurateDiagnosisId?: string;

  @ValidateIf((o) => o.accurateSubSubDiagnosisId)
  @IsUUID()
  accurateSubDiagnosisId?: string;

  @ValidateIf((o) => o.accurateSubSubSubDiagnosisId)
  @IsUUID()
  accurateSubSubDiagnosisId?: string;

  @IsOptional()
  @IsUUID()
  accurateSubSubSubDiagnosisId?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  accurateDiagnosisSeverity?: number;

  // Malignancy of most accurate accurate diagnosis
  @IsOptional()
  @IsBoolean()
  accurateDiagnosisIsMalignant?: boolean;

  @IsOptional()
  @IsUUID()
  latestActionId?: string;

  @ValidateIf((o) => !!o.latestActionId)
  @IsEnum(ConditionIndexActionType)
  latestActionType?: ConditionIndexActionType;

  @IsOptional()
  @IsEnum(DermloopClinicalActionVariantEnum)
  clinicalActionVariant?: DermloopClinicalActionVariantEnum;

  @ValidateIf((o) => !!o.latestActionId)
  @IsBoolean()
  latestActionResolved?: boolean;

  @ValidateIf((o) => !!o.latestActionId)
  @IsDate()
  latestActionDate?: Date;

  @IsOptional()
  @IsNumber()
  @Min(0)
  latestActionFollowUpMonths?: number;

  @IsOptional()
  @IsUUID()
  latestActionBiopsyTypeId?: string;

  // Oldest clinical image id for triage
  @IsOptional()
  @IsUUID()
  clinicalImageId?: string;

  // Oldest alternative clinical image id for triage
  @IsOptional()
  @IsString()
  alternativeClinicalImageId?: string;

  // Focal point x of latest clinical image
  @IsOptional()
  @IsNumber()
  focalPointX?: number;

  // Focal point y of latest clinical image
  @IsOptional()
  @IsNumber()
  focalPointY?: number;

  // Oldest close up image id for triage
  @IsOptional()
  @IsUUID()
  closeUpImageId?: string;

  // Oldest dermoscopic image id for triage
  @IsOptional()
  @IsUUID()
  dermoscopicImageId?: string;

  @Type(() => Date)
  @IsDate()
  updatedDate!: Date;

  @Type(() => Date)
  @IsDate()
  createdDate!: Date;

  @IsUUID()
  jurisdictionalGroupId!: string;

  @IsUUID()
  healthRecordId!: string;

  /** @deprecated Use {@link conditionOrdering} instead. Populated alongside it for backwards compatibility. */
  @IsNumber()
  @Min(0)
  lesionOrdering!: number;

  /** Condition ordering. Successor to the deprecated {@link lesionOrdering}; both are populated. */
  @IsOptional()
  @IsNumber()
  @Min(0)
  conditionOrdering?: number;

  @Type(() => ModelViewerLocation)
  @IsOptional()
  @ValidateNested()
  preciseLocation?: ModelViewerLocation;

  @IsOptional()
  @IsUUID()
  genderId?: string;

  @IsOptional()
  @IsNumber()
  patientAge?: number;

  // @IsOptional()
  // @IsBoolean()
  // patientContacted?: boolean;

  /** @deprecated Use {@link conditionTriageId} instead. Populated alongside it for backwards compatibility. */
  @IsUUID()
  lesionTriageId!: string;

  /** Condition triage id. Successor to the deprecated {@link lesionTriageId}; both are populated. */
  @IsOptional()
  @IsUUID()
  conditionTriageId?: string;

  @Type(() => Date)
  @IsDate()
  triageCreatedDate!: Date;

  /**
   * User / medical unit specific information
   */
  @IsUUID()
  userId!: string;

  /**
   * Display name of the user who created this triage. `POST /conditions/search`
   * only returns it when that user belongs to the requesting user's own medical
   * unit — an overview names a colleague, but identifies an external actor by
   * {@link userMedicalUnitName} only.
   */
  @IsOptional()
  @IsString()
  userName?: string;

  @IsUUID()
  userMedicalUnitId!: string;

  /**
   * Name of {@link userMedicalUnitId}. Optional despite the unit relation being non-nullable:
   * the projection resolves it through a join that skips soft-deleted units.
   */
  @IsOptional()
  @IsString()
  userMedicalUnitName?: string;

  @IsOptional()
  @IsUUID()
  userOccupationId?: string;

  /** Legacy spelling kept for client compatibility. */
  @IsOptional()
  @IsUUID()
  userSpecialityId?: string;

  @ValidateIf((o) => !!o.latestActionId)
  @IsEnum(ConditionIndexActionType)
  userActionType?: ConditionIndexActionType;

  @IsOptional()
  @IsUUID()
  userActionBiopsyTypeId?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  userActionFollowUpMonths?: number;

  @IsOptional()
  @IsString()
  userActionOtherTopicalTreatment?: string;

  @IsOptional()
  @IsUUID()
  userActionId?: string;

  @IsOptional()
  @IsBoolean()
  userActionResolved?: boolean;

  @Type(() => Date)
  @IsDate()
  userActionDate!: Date;

  @ValidateIf((o) => o.userSubDiagnosisId)
  @IsUUID()
  userDiagnosisId?: string;

  @ValidateIf((o) => o.userSubSubDiagnosisId)
  @IsUUID()
  userSubDiagnosisId?: string;

  @ValidateIf((o) => o.userSubSubSubDiagnosisId)
  @IsUUID()
  userSubSubDiagnosisId?: string;

  @IsOptional()
  @IsUUID()
  userSubSubSubDiagnosisId?: string;

  @IsOptional()
  @IsBoolean()
  userDiagnosisCorrect?: boolean;

  // Malignancy of most accurate user diagnosis
  @IsOptional()
  @IsBoolean()
  userDiagnosisIsMalignant?: boolean;

  @IsOptional()
  @IsUUID()
  userDiagnosisDifficultyId?: string;

  // Latest action performed in a medical unit, for filtering
  @IsBoolean()
  isLatestMedicalUnitAction!: boolean;

  // Latest action performed by the user, for filtering
  @IsBoolean()
  isLatestUserAction!: boolean;

  // Latest action performed on the condition, for filtering
  @IsBoolean()
  isLatestAction: boolean;

  // @IsOptional()
  // @IsUUID()
  // userSupervisesTriageId?: string;

  // @IsOptional()
  // @IsBoolean()
  // userNeedsSupervision?: boolean;

  // @IsOptional()
  // @IsBoolean()
  // userSupervisionFeedbackReviewed?: boolean;

  @IsBoolean()
  isPatientContacted: boolean;

  @IsOptional()
  @IsDate()
  followupOverdueDate?: Date;

  @IsBoolean()
  draft: boolean;

  @IsOptional()
  @IsBoolean()
  isDemoHealthRecord?: boolean;
}

/**
 * Given a condition index data dto, returns the most specific user diagnosis id.
 * @param condition
 * @returns
 */
export const getMostSpecificUserDiagnosisId = (
  condition: LesionIndexDataDto,
): string | undefined => {
  return (
    condition.userSubSubSubDiagnosisId ||
    condition.userSubSubDiagnosisId ||
    condition.userSubDiagnosisId ||
    condition.userDiagnosisId
  );
};

/**
 * Given a condition index data dto, returns the most specific accurate diagnosis id.
 * @param condition
 * @returns
 */
export const getMostSpecificAccurateDiagnosisId = (
  condition: LesionIndexDataDto,
): string | undefined => {
  return (
    condition.accurateSubSubSubDiagnosisId ||
    condition.accurateSubSubDiagnosisId ||
    condition.accurateSubDiagnosisId ||
    condition.accurateDiagnosisId
  );
};
